import { useCallback, useEffect, useRef, useState } from 'react';

// UI Pattern Library Entry #2 — resizable-disclosure. Collapsed by default; a drag
// handle pulls it taller to reveal more content, rather than a fixed-height scroll
// box or a modal (BL-193 Gate 5). First consumer is the Notes section on the Tier 3
// "Peacock" card (Gate 2, ProgressiveDisclosure.jsx) — this component is deliberately
// generic so any other card can adopt the same grammar later.
const COLLAPSED_HEIGHT = 0;
const STEP = 16;

function clampHeight(height, maxHeight) {
    return Math.max(COLLAPSED_HEIGHT, Math.min(maxHeight, height));
}

function ResizableDisclosure({
    label,
    collapsedHint = 'drag or click the handle to expand',
    expandedHint = 'drag the handle to resize',
    defaultHeight = 96,
    maxHeight = 320,
    autoExpand = false,
    labelStyle,
    children,
}) {
    const [height, setHeight] = useState(COLLAPSED_HEIGHT);
    const [dragging, setDragging] = useState(false);
    const dragStart = useRef(null);
    const didDrag = useRef(false);
    const expanded = height > COLLAPSED_HEIGHT;

    useEffect(() => {
        if (autoExpand) setHeight(current => (current > COLLAPSED_HEIGHT ? current : defaultHeight));
    }, [autoExpand, defaultHeight]);

    const toggle = useCallback(() => {
        setHeight(current => (current > COLLAPSED_HEIGHT ? COLLAPSED_HEIGHT : defaultHeight));
    }, [defaultHeight]);

    const onHandlePointerDown = useCallback(event => {
        event.preventDefault();
        dragStart.current = { startY: event.clientY, startHeight: height };
        didDrag.current = false;
        setDragging(true);
        event.currentTarget.setPointerCapture?.(event.pointerId);
    }, [height]);

    const onHandlePointerMove = useCallback(event => {
        if (!dragStart.current) return;
        didDrag.current = true;
        const delta = event.clientY - dragStart.current.startY;
        setHeight(clampHeight(dragStart.current.startHeight + delta, maxHeight));
    }, [maxHeight]);

    const endDrag = useCallback(() => {
        dragStart.current = null;
        setDragging(false);
    }, []);

    const onHandleClick = useCallback(() => {
        // A drag that actually moved the pointer already changed the height —
        // don't also toggle collapsed/expanded on the click that follows it.
        if (didDrag.current) {
            didDrag.current = false;
            return;
        }
        toggle();
    }, [toggle]);

    const onHandleKeyDown = useCallback(event => {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setHeight(current => clampHeight((current || defaultHeight) + STEP, maxHeight));
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setHeight(current => clampHeight(current - STEP, maxHeight));
        } else if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggle();
        }
    }, [toggle, defaultHeight, maxHeight]);

    return (
        <div className="space-y-1.5">
            {label && (
                <div className="text-xs font-semibold uppercase tracking-wide" style={labelStyle}>
                    {label} <span className="text-gray-600 normal-case font-normal">
                        · {expanded ? expandedHint : collapsedHint}
                    </span>
                </div>
            )}
            <div className="rounded border border-white/10 bg-[var(--surface-sunken)] overflow-hidden">
                <div
                    className="overflow-auto"
                    style={{ height, transition: dragging ? 'none' : 'height 150ms ease-out' }}
                >
                    <div className="p-2">{children}</div>
                </div>
                <div
                    role="separator"
                    aria-orientation="horizontal"
                    aria-label={`${label || 'Panel'} resize handle`}
                    aria-valuenow={height}
                    aria-valuemin={COLLAPSED_HEIGHT}
                    aria-valuemax={maxHeight}
                    data-expanded={expanded}
                    tabIndex={0}
                    onPointerDown={onHandlePointerDown}
                    onPointerMove={onHandlePointerMove}
                    onPointerUp={endDrag}
                    onPointerCancel={endDrag}
                    onClick={onHandleClick}
                    onKeyDown={onHandleKeyDown}
                    className="h-3 flex items-center justify-center cursor-ns-resize select-none border-t border-white/5 hover:bg-white/5"
                >
                    <span className="w-8 h-1 rounded-full bg-white/20" />
                </div>
            </div>
        </div>
    );
}

export default ResizableDisclosure;
